/**
 * Turn the GS decoder's flat feature tensor into a Gaussian-splat .splat buffer.
 *
 * The decoder's per-point output layout (32 gaussians per point) is:
 *   _xyz            ng * 3   (32 * 3 = 96 floats)
 *   _features_dc    ng * 1*3 (96)
 *   _scaling        ng * 3   (96)
 *   _rotation       ng * 4   (128)
 *   _opacity        ng       (32)
 *   _offset_scale   ng       (32, optional)
 *
 * total = 96+96+96+128+32+32 = 480 floats per point, matching the trace shape
 * (1, L, 480) reported by the export sanity check.
 *
 * The activations and biases mirror those baked into the Gaussian class in
 * triposplat.py / GS_DECODER_ARGS in triposplat.py.
 */

import type { Tensor } from "onnxruntime-web";
import { tensorFloat32 } from "./models";

// representation config -- copied from GS_DECODER_ARGS in triposplat.py
const NG = 32;
const SCALING_BIAS = 0.004;
const OPACITY_BIAS = 0.1;
const FILTER_KERNEL_3D = 0.0009;
const PERTURBE_SIZE = 1.5;
const OFFSET_SCALE = 0.05;
const LR = { xyz: 1.0, features_dc: 1.0, opacity: 1.0, scaling: 1.0, rotation: 0.1 };

const C0 = 0.28209479177387814;

// AABB the model decodes into: x,y,z in [-0.5, 0.5], extent 1.0.
const AABB_LO = [-0.5, -0.5, -0.5];
const AABB_HI = [ 1.0,  1.0,  1.0];

// Default transform (model -> world coords). Matches Python's
// Gaussian._DEFAULT_TRANSFORM exactly, so our .splat bytes are portable to
// SparkJS, SuperSplat, etc. The viewer-side orientation fix lives in
// viewer.ts (mkkellogg's viewer uses the opposite Y convention).
const TRANSFORM = [
  [1, 0,  0],
  [0, 0, -1],
  [0, 1,  0],
];

function softplus(x: number): number {
  return x > 30 ? x : Math.log1p(Math.exp(x));
}

function sigmoid(x: number): number {
  return 1 / (1 + Math.exp(-x));
}

function invSoftplus(y: number): number {
  // y = log(1 + exp(x)) -> x = log(exp(y) - 1). For our biases this is exact.
  return Math.log(Math.expm1(y));
}

function invSigmoid(p: number): number {
  return Math.log(p / (1 - p));
}

const SCALING_BIAS_INV = invSoftplus(SCALING_BIAS);
const OPACITY_BIAS_INV = invSigmoid(OPACITY_BIAS);

/** Hammersley sequence reproducing model.py:hammersley_sequence(3, i, NG). */
function hammersleyOffsets(): Float32Array {
  const PRIMES = [2, 3];
  function radicalInverse(base: number, n: number): number {
    let val = 0, invBase = 1 / base, invBaseN = invBase;
    while (n > 0) {
      const digit = n % base;
      val += digit * invBaseN;
      n = Math.floor(n / base);
      invBaseN *= invBase;
    }
    return val;
  }
  const out = new Float32Array(NG * 3);
  for (let i = 0; i < NG; i++) {
    out[i * 3 + 0] = i / NG;
    out[i * 3 + 1] = radicalInverse(PRIMES[0], i);
    out[i * 3 + 2] = radicalInverse(PRIMES[1], i);
  }
  // atanh((x*2-1) / PERTURBE_SIZE)
  for (let i = 0; i < NG * 3; i++) {
    const u = (out[i] * 2 - 1) / PERTURBE_SIZE;
    out[i] = 0.5 * Math.log((1 + u) / (1 - u));
  }
  return out;
}

const PERTURB = hammersleyOffsets();

const BASE_OFFSET_SCALE = Math.log(Math.exp(OFFSET_SCALE) - 1.0);

export interface GaussianBuildOpts {
  points: Float32Array;   // (L, 3) -- output of sampleOctree
  features: Tensor;       // (1, L, 480) -- GS decoder output
  /** Bias added to the opacity feature before sigmoid. Used to compensate
   *  for the systematic negative shift the int4 / fp16 gs_decoder variants
   *  introduce (the out_proj layer takes a precision hit from cast). With
   *  the int4 export, ~+3.2 brings the opacity distribution into python's
   *  range; with fp16 you need ~+5. Default 0 = use the model's untouched
   *  bias. */
  opacityBoost?: number;
}

/**
 * Returns a Float32Array of length L*NG*(3+3+4+1+3) packed as
 * xyz(3) + dc(3) + rot(4) + opacity(1) + scale(3), and a parallel rgba uint8.
 * Total flexible-blob structure consumed by `toSplatBytes`.
 */
export function unpackGaussians(opts: GaussianBuildOpts) {
  const L = opts.points.length / 3;
  // The GS decoder defaults to fp16 IO (the fp16 variant ships in COMPONENTS).
  // tensorFloat32 decodes the Uint16 buffer; otherwise this entire loop would
  // be reading fp16 bit patterns as fp32 and producing garbage gaussians --
  // colors near (128,128,128), opacities sigmoid'd from random uint16s, and
  // a sort key that doesn't reflect anything meaningful.
  const feat = tensorFloat32(opts.features);
  const stride = 480;

  // ranges (per layout in ElasticGaussianFixedlenDecoder._calc_layout)
  const R_XYZ = [0, NG * 3];
  const R_DC  = [NG * 3, NG * 6];
  const R_SC  = [NG * 6, NG * 9];
  const R_ROT = [NG * 9, NG * 13];
  const R_OP  = [NG * 13, NG * 14];
  const R_OFS = [NG * 14, NG * 15];

  const N = L * NG;
  const xyz   = new Float32Array(N * 3);
  const rgba  = new Uint8ClampedArray(N * 4);
  const scale = new Float32Array(N * 3);
  const rot   = new Float32Array(N * 4);
  const op    = new Float32Array(N);

  for (let i = 0; i < L; i++) {
    const px = opts.points[i * 3 + 0];
    const py = opts.points[i * 3 + 1];
    const pz = opts.points[i * 3 + 2];
    const base = i * stride;

    // offset scale
    const ofs = new Float32Array(NG);
    for (let j = 0; j < NG; j++) {
      ofs[j] = softplus(feat[base + R_OFS[0] + j] + BASE_OFFSET_SCALE);
    }

    for (let j = 0; j < NG; j++) {
      // offset = tanh(_xyz*lr + perturb) * 0.5 * perturbe_size * ofs
      const ox = Math.tanh(feat[base + R_XYZ[0] + j * 3 + 0] * LR.xyz + PERTURB[j * 3 + 0]) * 0.5 * PERTURBE_SIZE * ofs[j];
      const oy = Math.tanh(feat[base + R_XYZ[0] + j * 3 + 1] * LR.xyz + PERTURB[j * 3 + 1]) * 0.5 * PERTURBE_SIZE * ofs[j];
      const oz = Math.tanh(feat[base + R_XYZ[0] + j * 3 + 2] * LR.xyz + PERTURB[j * 3 + 2]) * 0.5 * PERTURBE_SIZE * ofs[j];

      // _xyz_world = (offset + point) * aabb_hi + aabb_lo
      const xt = (ox + px) * AABB_HI[0] + AABB_LO[0];
      const yt = (oy + py) * AABB_HI[1] + AABB_LO[1];
      const zt = (oz + pz) * AABB_HI[2] + AABB_LO[2];

      // transform -> viewer coords
      const ix = i * NG + j;
      xyz[ix * 3 + 0] = TRANSFORM[0][0] * xt + TRANSFORM[0][1] * yt + TRANSFORM[0][2] * zt;
      xyz[ix * 3 + 1] = TRANSFORM[1][0] * xt + TRANSFORM[1][1] * yt + TRANSFORM[1][2] * zt;
      xyz[ix * 3 + 2] = TRANSFORM[2][0] * xt + TRANSFORM[2][1] * yt + TRANSFORM[2][2] * zt;

      // scale: sqrt(softplus(_scaling + scale_bias)^2 + filter^2)
      for (let a = 0; a < 3; a++) {
        const s = softplus(feat[base + R_SC[0] + j * 3 + a] * LR.scaling + SCALING_BIAS_INV);
        scale[ix * 3 + a] = Math.sqrt(s * s + FILTER_KERNEL_3D * FILTER_KERNEL_3D);
      }

      // rotation: (R + rots_bias=[1,0,0,0]) * lr.rotation. Apply transform.
      const r0 = feat[base + R_ROT[0] + j * 4 + 0] * LR.rotation + 1.0;
      const r1 = feat[base + R_ROT[0] + j * 4 + 1] * LR.rotation;
      const r2 = feat[base + R_ROT[0] + j * 4 + 2] * LR.rotation;
      const r3 = feat[base + R_ROT[0] + j * 4 + 3] * LR.rotation;
      const [qw, qx, qy, qz] = transformQuaternion(r0, r1, r2, r3);
      const norm = Math.hypot(qw, qx, qy, qz) || 1;
      rot[ix * 4 + 0] = qw / norm;
      rot[ix * 4 + 1] = qx / norm;
      rot[ix * 4 + 2] = qy / norm;
      rot[ix * 4 + 3] = qz / norm;

      // opacity: sigmoid(_opacity + opacity_bias + opacityBoost). The boost
      // is a hand-tuned compensation for the gs_decoder export's negative
      // shift; defaults to 0.
      const o = sigmoid(feat[base + R_OP[0] + j] * LR.opacity + OPACITY_BIAS_INV
                        + (opts.opacityBoost ?? 0));
      op[ix] = o;

      // color from DC: rgb = clip((dc * C0 + 0.5) * 255, 0, 255)
      const r = Math.max(0, Math.min(255, Math.round((feat[base + R_DC[0] + j * 3 + 0] * LR.features_dc * C0 + 0.5) * 255)));
      const g = Math.max(0, Math.min(255, Math.round((feat[base + R_DC[0] + j * 3 + 1] * LR.features_dc * C0 + 0.5) * 255)));
      const b = Math.max(0, Math.min(255, Math.round((feat[base + R_DC[0] + j * 3 + 2] * LR.features_dc * C0 + 0.5) * 255)));
      rgba[ix * 4 + 0] = r;
      rgba[ix * 4 + 1] = g;
      rgba[ix * 4 + 2] = b;
      rgba[ix * 4 + 3] = Math.max(0, Math.min(255, Math.round(o * 255)));
    }
  }

  return { xyz, scale, rot, rgba, op };
}

function transformQuaternion(qw: number, qx: number, qy: number, qz: number): [number, number, number, number] {
  // build rotation matrix from quaternion, multiply by TRANSFORM on the left,
  // convert back to a quaternion.
  const n = Math.hypot(qw, qx, qy, qz) || 1;
  qw /= n; qx /= n; qy /= n; qz /= n;
  const R = [
    [1 - 2*(qy*qy + qz*qz), 2*(qx*qy - qw*qz),     2*(qx*qz + qw*qy)],
    [2*(qx*qy + qw*qz),     1 - 2*(qx*qx + qz*qz), 2*(qy*qz - qw*qx)],
    [2*(qx*qz - qw*qy),     2*(qy*qz + qw*qx),     1 - 2*(qx*qx + qy*qy)],
  ];
  const M = [[0,0,0],[0,0,0],[0,0,0]];
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++)
      for (let k = 0; k < 3; k++)
        M[i][j] += TRANSFORM[i][k] * R[k][j];
  const trace = M[0][0] + M[1][1] + M[2][2];
  let s = Math.sqrt(Math.max(trace + 1, 0)) * 2;
  if (s < 1e-6) {
    // gimbal -- pick the largest diagonal
    if (M[0][0] >= M[1][1] && M[0][0] >= M[2][2]) {
      s = Math.sqrt(Math.max(1 + M[0][0] - M[1][1] - M[2][2], 0)) * 2;
      return [(M[2][1] - M[1][2]) / s, 0.25 * s, (M[0][1] + M[1][0]) / s, (M[0][2] + M[2][0]) / s];
    } else if (M[1][1] > M[2][2]) {
      s = Math.sqrt(Math.max(1 + M[1][1] - M[0][0] - M[2][2], 0)) * 2;
      return [(M[0][2] - M[2][0]) / s, (M[0][1] + M[1][0]) / s, 0.25 * s, (M[1][2] + M[2][1]) / s];
    } else {
      s = Math.sqrt(Math.max(1 + M[2][2] - M[0][0] - M[1][1], 0)) * 2;
      return [(M[1][0] - M[0][1]) / s, (M[0][2] + M[2][0]) / s, (M[1][2] + M[2][1]) / s, 0.25 * s];
    }
  }
  return [0.25 * s, (M[2][1] - M[1][2]) / s, (M[0][2] - M[2][0]) / s, (M[1][0] - M[0][1]) / s];
}

/** Convert unpacked gaussians to .splat binary format (32 bytes per splat). */
export function toSplatBytes(g: ReturnType<typeof unpackGaussians>): ArrayBuffer {
  const N = g.xyz.length / 3;
  // sort by -opacity * prod(scale)
  const key = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    key[i] = -g.op[i] * g.scale[i*3+0] * g.scale[i*3+1] * g.scale[i*3+2];
  }
  const order = new Int32Array(N);
  for (let i = 0; i < N; i++) order[i] = i;
  order.sort((a, b) => key[a] - key[b]);

  const out = new Uint8Array(N * 32);
  const dv = new DataView(out.buffer);
  for (let i = 0; i < N; i++) {
    const o = order[i] >>> 0;
    const w = i * 32;
    dv.setFloat32(w + 0,  g.xyz[o*3+0], true);
    dv.setFloat32(w + 4,  g.xyz[o*3+1], true);
    dv.setFloat32(w + 8,  g.xyz[o*3+2], true);
    dv.setFloat32(w + 12, g.scale[o*3+0], true);
    dv.setFloat32(w + 16, g.scale[o*3+1], true);
    dv.setFloat32(w + 20, g.scale[o*3+2], true);
    out[w + 24] = g.rgba[o*4+0];
    out[w + 25] = g.rgba[o*4+1];
    out[w + 26] = g.rgba[o*4+2];
    out[w + 27] = g.rgba[o*4+3];
    // rotation as uint8: clip(rot * 128 + 128, 0, 255)
    for (let k = 0; k < 4; k++) {
      out[w + 28 + k] = Math.max(0, Math.min(255, Math.round(g.rot[o*4+k] * 128 + 128)));
    }
  }
  return out.buffer;
}
